/**
 * *Change supplier…* (round 19's `ChangeSupplierDialog`; docs/features/
 * order-to-invoice/56 §3–§4, 57 §2.3): buy the line from another supplier
 * instead, under the Sourcing policy's minimal version — the alternatives at
 * today's cost and lead time, the default and why, suppliers on hold left out
 * — and a reason, recorded. The customer's price never changes.
 *
 * The choices are the catalogue's, read when the dialog opens from the URL
 * the line's `release.change_supplier` carries; a cost that changed since
 * (409) shows the new figures to confirm. Every word is the catalogue's.
 *
 * A bought service (`priced_by_hand`, supplier interface spec 12) has no
 * alternatives to read: its supplier is searched and its cost entered, and it
 * posts to the service line's own command (the view's `action_url`).
 */
import { useCallback, useEffect, useId, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  ChangeSupplierRequest,
  ChangeSupplierView,
  SupplierAlternatives,
} from "@shared-core/data/order-to-invoice-types";
import {
  orderStepFailure,
  type OrderStepFailure,
} from "@shared-core/domain/order-to-invoice-presentation";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import { Textarea } from "../../primitives/textarea";
import type { EntityOption } from "../../patterns/EntityCombobox";
import { FailureBanner, Field } from "./OrderDialogs";
import {
  CHOOSE_SUPPLIER,
  ENTER_COST,
  SERVICE_GOODS_HINT,
  ServiceSupplierFields,
} from "./ServiceSupplierFields";
import {
  costPenceOf,
  withoutSupplierRefusal,
} from "@shared-core/domain/service-supplier";
import { newUuid } from "@shared-core/domain/object-ui/command-run";

type Load =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; offered: SupplierAlternatives };

/** One submission's request id: a retry with it records nothing new. */
const requestId = newUuid;

interface DialogProps {
  /** "Change supplier · .02 Pull-up banner" */
  title: string;
  view: ChangeSupplierView;
  busy: boolean;
  onClose: () => void;
  onConfirm: (input: ChangeSupplierRequest) => Promise<OrderStepFailure | null>;
}

export function ChangeSupplierDialog(props: DialogProps) {
  return props.view.priced_by_hand ? (
    <ChangeServiceSupplier {...props} />
  ) : (
    <ChangeToAnAlternative {...props} />
  );
}

/**
 * A bought fulfilment or installation (supplier interface spec 12): it has no
 * route to price alternatives, so the supplier is searched and its cost
 * entered, as when it was added. The print it applies to goes to the new
 * supplier's goods-in; a supplier with none is refused beside the field.
 */
function ChangeServiceSupplier({
  title,
  view,
  busy,
  onClose,
  onConfirm,
}: DialogProps) {
  const id = useId();
  const [supplier, setSupplier] = useState<EntityOption | null>(null);
  const [cost, setCost] = useState("");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const [submission] = useState(requestId);
  const errorOf = (
    field: "supplier_no" | "cost_pence" | "reason",
    typed: string | undefined,
  ) => (touched ? typed : undefined) ?? failure?.fieldErrors[field];
  const supplierError = errorOf(
    "supplier_no",
    supplier ? undefined : CHOOSE_SUPPLIER,
  );
  const costError = errorOf(
    "cost_pence",
    costPenceOf(cost) === null ? ENTER_COST : undefined,
  );
  const reasonError = errorOf(
    "reason",
    reason.trim() ? undefined : "Say why the supplier is changing.",
  );
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            The service is bought from the supplier you choose, at the cost you
            enter; the customer&apos;s price doesn&apos;t change. Suppliers on
            hold aren&apos;t offered.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setTouched(true);
            setFailure(null);
            const pence = costPenceOf(cost);
            if (!supplier || pence === null || !reason.trim()) return;
            const refused = await onConfirm({
              supplier_no: supplier.id,
              cost_pence: pence,
              reason: reason.trim(),
              request_id: submission,
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <ServiceSupplierFields
            idPrefix={id}
            suppliersUrl={view.suppliers_url ?? ""}
            supplier={supplier}
            onSupplierChange={(chosen) => {
              setSupplier(chosen);
              setFailure(withoutSupplierRefusal);
            }}
            cost={cost}
            onCostChange={setCost}
            supplierError={supplierError}
            costError={costError}
            goodsHint={SERVICE_GOODS_HINT}
            disabled={busy}
          />
          <Field id={`${id}-reason`} label="Why" error={reasonError}>
            {(aria) => (
              <Textarea
                {...aria}
                rows={2}
                value={reason}
                placeholder="Kitting Co can't fit it in this week"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button type="submit" disabled={busy}>
              Change supplier
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ChangeToAnAlternative({
  title,
  view,
  busy,
  onClose,
  onConfirm,
}: DialogProps) {
  const id = useId();
  const adapter = useDataAdapter();
  const [load, setLoad] = useState<Load>({ kind: "loading" });
  const [choice, setChoice] = useState<string>("");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const [submission] = useState(requestId);

  const read = useCallback(async () => {
    try {
      const offered = await adapter.getSupplierAlternatives(view);
      setLoad({ kind: "loaded", offered });
      setChoice((current) => {
        const keys = offered.alternatives.map(keyOf);
        if (keys.includes(current)) return current;
        const preferred = offered.alternatives.find((a) => a.is_default);
        return preferred ? keyOf(preferred) : (keys[0] ?? "");
      });
    } catch (err) {
      setLoad({ kind: "failed", message: orderStepFailure(err).summary });
    }
  }, [adapter, view]);

  useEffect(() => {
    void read();
  }, [read]);

  const offered = load.kind === "loaded" ? load.offered : null;
  const chosen = offered?.alternatives.find((a) => keyOf(a) === choice);
  const reasonError =
    (touched && !reason.trim()
      ? "Say why the supplier is changing."
      : undefined) ?? failure?.fieldErrors.reason;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {offered?.description ??
              "The other suppliers that could make it today. Suppliers on hold aren't offered."}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setTouched(true);
            if (!chosen || !reason.trim()) return;
            const refused = await onConfirm({
              lines: offered!.line_refs.map((line_ref) => ({ line_ref })),
              route_code: chosen.route_code,
              reason: reason.trim(),
              expected_cost_pence: chosen.cost_pence,
              request_id: submission,
            });
            setFailure(refused);
            if (refused) {
              // A changed cost or a supplier gone: show today's choices to confirm again.
              void read();
              return;
            }
            onClose();
          }}
        >
          <FailureBanner failure={failure} />
          {load.kind === "loading" && (
            <p className="text-sm text-muted-foreground" role="status">
              Finding the other suppliers…
            </p>
          )}
          {load.kind === "failed" && (
            <p className="text-sm text-red-600" role="alert">
              Could not load the other suppliers: {load.message}
            </p>
          )}
          {offered && offered.alternatives.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {offered.none_sentence}
            </p>
          )}
          {offered && offered.alternatives.length > 0 && (
            <div className="space-y-2">
              <RadioGroup
                value={choice}
                onValueChange={setChoice}
                aria-label="Supplier"
              >
                {offered.alternatives.map((a) => (
                  <div key={keyOf(a)} className="flex items-center gap-2">
                    <RadioGroupItem value={keyOf(a)} id={`${id}-${keyOf(a)}`} />
                    <Label
                      htmlFor={`${id}-${keyOf(a)}`}
                      className="font-normal"
                    >
                      {a.supplier_name}
                      {a.is_default && (
                        <span className="font-medium"> (default)</span>
                      )}{" "}
                      <span className="text-muted-foreground">· {a.label}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              {offered.default_sentence && (
                <p className="text-xs text-muted-foreground">
                  {offered.default_sentence}
                </p>
              )}
            </div>
          )}
          <Field id={`${id}-reason`} label="Why" error={reasonError}>
            {(aria) => (
              <Textarea
                {...aria}
                rows={2}
                value={reason}
                placeholder="PDQ can't make it in time"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button type="submit" disabled={busy || !chosen}>
              Change supplier
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function keyOf(a: { supplier_id: string; route_code: string }): string {
  return `${a.supplier_id}:${a.route_code}`;
}
